import { useEffect, useState } from "react"
import React from "react"
import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import Input from "components/ProjectNonSpecificComponents/Elements/Input"
import { useTranslation } from "react-i18next"
import HorizontalFormField from "../HorizontalFormField"
import Whitespace from "components/ProjectNonSpecificComponents/Elements/Whitespace"
import { observer } from "mobx-react"
import { useFormStore } from "components/ProjectSpecificComponents/Contexts/FormStoreContext"
import PreferencesCheckbox from "components/ProjectSpecificComponents/Elements/Global/atoms/PreferencesCheckbox"
import { useStore } from "components/ProjectSpecificComponents/Contexts/GlobalStoreContext"
import {
	IEmailConfig,
	IEmailField,
	ISettings,
} from "components/ProjectSpecificComponents/Interfaces/FormsInterface"
import AppRequests from "components/ProjectSpecificComponents/API/AppRequests/AppRequests"
import PreferencesSmallButton from "components/ProjectSpecificComponents/Elements/Global/atoms/Buttons/PreferencesSmallButton"
import isEmailRegex from "components/ProjectSpecificComponents/Functions/isEmailRegex"
import { ICheckEmail } from "components/ProjectSpecificComponents/Interfaces/DataInterface"
import { defaultEmailConfig } from "components/ProjectSpecificComponents/Stores/FormStore/defaults"
import { FormInput } from "components/ProjectNonSpecificComponents/Elements/FormElements/FormInput"
import { lostOrFinishedAdminSession } from "components/ProjectSpecificComponents/API/Functions/lostOrFinishedAdminSession"
import { useHistory } from "react-router"
import { rmChars } from "components/ProjectNonSpecificComponents/Functions/rmChars"

interface Props {}

export const EmailConfigForm = observer(({}: Props) => {
	const store = useStore()
	const formStore = useFormStore()
	const { t } = useTranslation()
	const history = useHistory()

	const [emailConfig, setEmailConfig] = useState<IEmailConfig>(defaultEmailConfig)
	const [testEmail, setTestEmail] = useState<ICheckEmail>({ mail: "" })
	const [refreshCount, refresh] = useState(0)
	const { notificationText } = formStore.settingsData.emailField
	const { sendEmail } = formStore.settingsData.emailField

	const updateEmailConfig = (data: Partial<IEmailConfig>) => {
		setEmailConfig({ ...emailConfig, ...data })
	}

	useEffect(() => {
		fetchAndSetEmailSettings()
	}, [])

	const fetchAndSetEmailSettings = async () => {
		store.setLoaderOverlayOpen(true)

		const { ok, data, statusCode } = await AppRequests("Setting").get<IEmailConfig>(
			"get-email-settings"
		)

		if (ok && data) {
			if (refreshCount > 0) {
				store.setInfoModalConfig({
					type: data.isCompleted ? "info" : "warning",
					titleKey: "data-saving.success",
					showDuration: 2000,
					descriptionKey: data.isCompleted ? "" : "email.email-settings-incompleate",
				})
			}
			setEmailConfig(data)
			refresh((count) => count + 1)
		} else if (statusCode == 401) {
			lostOrFinishedAdminSession(history, store)
		} else if (statusCode == 404) {
			saveEmailSettings()
		}

		store.setLoaderOverlayOpen(false)
	}

	const isAllFieldsCompleted = () => {
		if (emailConfig.isAnonymous) {
			if (!emailConfig.emailFrom || !emailConfig.smtpHost || !emailConfig.smtpPort) {
				return false
			}
			return true
		}
		if (
			!emailConfig.emailFrom ||
			!emailConfig.smtpHost ||
			!emailConfig.smtpPort ||
			!emailConfig.smtpUser ||
			!emailConfig.smtpPass
		) {
			return false
		}
		return true
	}

	const saveSettingsData = async () => {
		if (!emailConfig.smtpPort)
			return store.setInfoModalConfig({
				type: "fail",
				titleKey: "email.email-incompleate-port",
			})

		if (emailConfig.isCompleted) {
			if (formStore.settingsData.emailField.sendEmail && !isAllFieldsCompleted()) {
				return store.setInfoModalConfig({
					type: "fail",
					titleKey: "email.email-incompleate",
				})
			}
			updateEmailSettings()
			saveEmailFields()
		} else {
			updateEmailSettings()
		}
	}

	const updateEmailSettings = async () => {
		const { ok, statusCode } = await AppRequests("Setting").post(
			"update-email-settings",
			emailConfig
		)

		if (ok) {
			fetchAndSetEmailSettings()
		} else if (statusCode == 401) {
			lostOrFinishedAdminSession(history, store)
		} else {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "data-saving.failed-email",
			})
		}
	}

	const saveEmailSettings = async () => {
		const { ok, statusCode } = await AppRequests("Setting").post("save-email-settings", emailConfig)

		if (ok) {
			fetchAndSetEmailSettings()
		} else if (statusCode == 401) {
			lostOrFinishedAdminSession(history, store)
		} else {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "data-saving.failed-email",
			})
		}
	}

	const saveEmailFields = async () => {
		const { ok, data, statusCode } = await AppRequests("Setting").post<
			Partial<IEmailField>,
			ISettings
		>("save-email-field", formStore.settingsData.emailField)

		if (ok && data) {
			formStore.mergeSettingsDataSafely(data)
		} else if (statusCode == 401) {
			lostOrFinishedAdminSession(history, store)
		} else {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "data-saving.failed",
			})
		}
	}
	/**
	 * Checks if entered email is correct format and if true then send test email
	 */
	const checkEmailConfing = async () => {
		if (!isEmailRegex(testEmail.mail)) {
			return store.setInfoModalConfig({
				type: "fail",
				titleKey: "email.email-invalid",
				descriptionKey: "email.email-invalid-description",
			})
		}

		const checkEmailContent = {
			mail: testEmail.mail,
			content: t("rawText.email-test-content"),
			subject: t("rawText.email-test-subject"),
		}

		const { ok } = await AppRequests("Setting").post("check", checkEmailContent)

		if (ok) {
			store.setInfoModalConfig({
				type: "info",
				titleKey: "email.email-send",
				showDuration: 1000,
			})
		} else {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "email.email-send-failed",
			})
		}
	}

	const onSendEmailChange = () => {
		formStore.mergeEmailFormData({
			sendEmail: !formStore.settingsData.emailField.sendEmail,
		})
	}

	const onNotificationTextChange = (text: string) => {
		formStore.mergeEmailFormData({ notificationText: text })
	}

	return (
		<HyperDiv direction="column" padding=".5rem 0" spaceBetweenElems="2.25rem">
			<HorizontalFormField
				containerStyle={{ padding: "8px 16px" }}
				label={t(`forms.field-label.source-email`)}
				centerSpaceMultiplier={0.5}
				tooltipBelowInputField
			>
				<Input
					wide
					value={emailConfig.emailFrom}
					onChange={(e) => updateEmailConfig({ emailFrom: e.target.value })}
				/>
			</HorizontalFormField>
			<HorizontalFormField
				containerStyle={{ padding: "8px 16px" }}
				label={t(`forms.field-label.server-address`)}
				centerSpaceMultiplier={0.5}
				tooltipBelowInputField
			>
				<Whitespace h x="1" />
				<Input
					wide
					value={emailConfig.smtpHost}
					onChange={(e) => updateEmailConfig({ smtpHost: e.target.value })}
				/>
			</HorizontalFormField>
			<HorizontalFormField
				containerStyle={{ padding: "8px 16px" }}
				label={t(`forms.field-label.port`)}
				centerSpaceMultiplier={0.5}
				tooltipBelowInputField
			>
				<Input
					wide
					type="number"
					value={emailConfig.smtpPort}
					onChange={(e) => updateEmailConfig({ smtpPort: rmChars(e.target.value) })}
				/>
			</HorizontalFormField>
			<HorizontalFormField
				containerStyle={{ padding: "8px 16px" }}
				label={t(`forms.field-label.anonymous-connection`)}
				centerSpaceMultiplier={0.5}
				tooltipBelowInputField
			>
				<PreferencesCheckbox
					checked={emailConfig.isAnonymous}
					onCheck={() =>
						updateEmailConfig({
							isAnonymous: !emailConfig.isAnonymous,
						})
					}
				/>
			</HorizontalFormField>

			{!emailConfig.isAnonymous && (
				<>
					<HorizontalFormField
						containerStyle={{ padding: "8px 16px" }}
						label={t(`forms.field-label.username`)}
						centerSpaceMultiplier={0.5}
						tooltipBelowInputField
					>
						<Input
							wide
							value={emailConfig.smtpUser}
							onChange={(e) => updateEmailConfig({ smtpUser: e.target.value })}
						/>
					</HorizontalFormField>
					<HorizontalFormField
						containerStyle={{ padding: "8px 16px" }}
						label={t(`forms.field-label.password`)}
						centerSpaceMultiplier={0.5}
						tooltipBelowInputField
					>
						<Input
							wide
							type="password"
							value={emailConfig.smtpPass}
							onChange={(e) => updateEmailConfig({ smtpPass: e.target.value })}
							placeholder="********"
						/>
					</HorizontalFormField>
				</>
			)}

			<HorizontalFormField
				containerStyle={{ padding: "8px 16px" }}
				label={t(`forms.field-label.secure-connection`)}
				centerSpaceMultiplier={0.5}
				tooltipBelowInputField
			>
				<PreferencesCheckbox
					checked={emailConfig.isSecureConnection}
					onCheck={() =>
						updateEmailConfig({
							isSecureConnection: !emailConfig.isSecureConnection,
						})
					}
				/>
			</HorizontalFormField>

			{emailConfig.isCompleted ? (
				<>
					<HorizontalFormField
						containerStyle={{ padding: "8px 16px" }}
						label={t(`forms.field-label.test-email`)}
						centerSpaceMultiplier={0.5}
						tooltipBelowInputField
					>
						<HyperDiv direction="row">
							<Input
								wide
								value={testEmail?.mail}
								onChange={(e) => setTestEmail({ mail: e.target.value })}
							/>
							<Whitespace h x=".625" />
							<PreferencesSmallButton
								onClick={checkEmailConfing}
								children={t("forms.field-label.send-test-email")}
							/>
						</HyperDiv>
					</HorizontalFormField>
					<HorizontalFormField
						label={t("forms.field-label.send-notification")}
						containerStyle={{ padding: "8px 16px" }}
						centerSpaceMultiplier={0.5}
						tooltipBelowInputField
					>
						<PreferencesCheckbox checked={sendEmail} onCheck={onSendEmailChange} />
					</HorizontalFormField>
					<HorizontalFormField
						label={t("forms.field-label.notification-text")}
						containerStyle={{ padding: "8px 16px" }}
						centerSpaceMultiplier={0.5}
						tooltipBelowInputField
					>
						<FormInput
							multiline
							wide
							value={notificationText}
							onValueChange={(text) => {
								onNotificationTextChange(text)
							}}
						/>
					</HorizontalFormField>
				</>
			) : (
				<HyperDiv
					direction="row"
					style={{
						overflow: "hidden",
					}}
				>
					{t(`note.email-settings`)}
				</HyperDiv>
			)}
			<HyperDiv centeredX>
				<PreferencesSmallButton
					style={{ width: "25%" }}
					onClick={saveSettingsData}
					children={t("rawText.save")}
				/>
			</HyperDiv>
		</HyperDiv>
	)
})

export default EmailConfigForm
